import type { JSX } from 'react';
import { SectionHeader } from '@/components/blocks/page';
import { Section } from '@/components/blocks/layout';
import { EntityActionCard, EntityList, EntityRow, Panel } from '@/components/blocks/data-display';
import { EmptyState, Chip } from '@/components/blocks/feedback';
import { resolveSiteDisplayLabel } from '@/utils/entityIdentity';
import type { SiteSummary } from '../types';
import type { StatusTone } from '@/lib/statusTone';

/**
 * Computes a human-readable status label and tone for a next visit date.
 */
function getDateStatusDisplay(
  nextVisit: string | null | undefined,
  scheduleState: SiteSummary['scheduleState']
): { label: string; tone: StatusTone } | null {
  if (!nextVisit) {
    if (scheduleState === 'to_plan') {
      return { label: 'Da pianificare', tone: 'warning' };
    }
    return null;
  }

  const visitDate = new Date(nextVisit);
  const now = new Date();
  const diffMs = visitDate.getTime() - now.getTime();
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays < 0) {
    return { label: `Scaduto da ${Math.abs(diffDays)} gg`, tone: 'danger' };
  }
  if (diffDays === 0) {
    return { label: 'Oggi', tone: 'warning' };
  }
  if (diffDays <= 7) {
    return {
      label: `Tra ${diffDays} ${diffDays === 1 ? 'giorno' : 'giorni'}`,
      tone: 'warning',
    };
  }
  if (diffDays <= 30) {
    return { label: `Tra ${diffDays} giorni`, tone: 'neutral' };
  }
  const months = Math.ceil(diffDays / 30);
  return {
    label: `Tra ${months} ${months === 1 ? 'mese' : 'mesi'}`,
    tone: 'neutral',
  };
}

/**
 * Formats location as "City (Province)" or just "City".
 */
function formatLocation(city?: string | null, province?: string | null): string | undefined {
  if (!city?.trim()) return undefined;
  const cityTrimmed = city.trim();
  if (province?.trim()) {
    return `${cityTrimmed} (${province.trim()})`;
  }
  return cityTrimmed;
}

export function CompanySitesTab({
  siteSummaries,
  onCreateSite,
  onNavigateSite,
  onPlanSite,
  onCreateReport,
  sectionId,
}: {
  siteSummaries: SiteSummary[];
  onCreateSite: () => void;
  onNavigateSite: (siteId: string) => void;
  onPlanSite?: (site: SiteSummary) => void;
  onCreateReport?: (siteId: string) => void;
  sectionId?: string;
}): JSX.Element {
  const useDenseList = siteSummaries.length >= 10;

  const resolveStateChip = (state: SiteSummary['scheduleState']) => {
    switch (state) {
      case 'overdue':
        return <Chip tone="danger">In ritardo</Chip>;
      case 'to_plan':
        return <Chip tone="warning">Da pianificare</Chip>;
      case 'planned':
        return <Chip tone="neutral">Pianificato</Chip>;
      case 'draft':
        return <Chip tone="info">Bozza in corso</Chip>;
      default:
        return null;
    }
  };

  return (
    <Section id={sectionId}>
      <SectionHeader
        title="Elenco stabilimenti"
        count={siteSummaries.length > 0 ? siteSummaries.length : undefined}
        action={{
          label: 'Nuovo stabilimento',
          onClick: onCreateSite,
        }}
      />
      {siteSummaries.length > 0 ? (
        useDenseList ? (
          <EntityList>
            {siteSummaries.map((site) => {
              const { name: displayName } = resolveSiteDisplayLabel(null, {
                name: site.name,
                sequenceNumber: site.sequenceNumber,
              });
              const locationLabel = formatLocation(site.city, site.province);
              const draftBadge = site.draftReportId ? <Chip tone="info">Bozza</Chip> : null;
              const stateBadge =
                site.scheduleState !== 'draft' ? resolveStateChip(site.scheduleState) : null;
              const ncBadge = site.ncOpen > 0 ? <Chip tone="danger">NC {site.ncOpen}</Chip> : null;
              const badges =
                ncBadge || draftBadge || stateBadge ? (
                  <div className="flex flex-wrap gap-2">
                    {draftBadge}
                    {stateBadge}
                    {ncBadge}
                  </div>
                ) : undefined;
              const metaLabel =
                site.nextVisitLabel !== 'Da pianificare'
                  ? site.nextVisitLabel
                  : site.planningLabel !== 'Da pianificare'
                    ? site.planningLabel
                    : null;

              return (
                <EntityRow
                  key={site.id}
                  title={displayName}
                  subtitle={locationLabel}
                  meta={
                    metaLabel ? (
                      <span className="font-mono tabular-nums">{metaLabel}</span>
                    ) : undefined
                  }
                  badges={badges}
                  onClick={() => onNavigateSite(site.id)}
                />
              );
            })}
          </EntityList>
        ) : (
          <div className="grid gap-5 md:grid-cols-3">
            {siteSummaries.map((site) => {
              const { name: displayName } = resolveSiteDisplayLabel(null, {
                name: site.name,
                sequenceNumber: site.sequenceNumber,
              });

              const locationLabel = formatLocation(site.city, site.province);
              const dateStatus = getDateStatusDisplay(site.nextVisit, site.scheduleState);

              // Build chips
              const chipElements: JSX.Element[] = [];
              chipElements.push(
                <Chip key="impianti" tone="neutral" size="sm">
                  Impianti {site.refrigerationUnitsCount}
                </Chip>
              );
              if (site.ncOpen > 0) {
                chipElements.push(
                  <Chip key="nc" tone="danger" size="sm">
                    NC {site.ncOpen}
                  </Chip>
                );
              }
              if (site.draftReportId) {
                chipElements.push(
                  <Chip key="bozza" tone="info" size="sm">
                    Bozza
                  </Chip>
                );
              }

              const hasDraft = !!site.draftReportId;

              return (
                <EntityActionCard
                  key={site.id}
                  icon="factory"
                  title={displayName}
                  subtitle={locationLabel}
                  primaryValueLabel="Prossimo sopralluogo"
                  primaryValue={site.nextVisitLabel}
                  primaryValueStatus={dateStatus ?? undefined}
                  chips={<>{chipElements}</>}
                  onNavigate={() => onNavigateSite(site.id)}
                  primaryAction={
                    hasDraft
                      ? {
                          label: 'Riprendi bozza',
                          icon: 'edit_document',
                          onClick: () => onNavigateSite(site.id),
                        }
                      : onCreateReport
                        ? {
                            label: 'Crea report',
                            icon: 'add_circle',
                            onClick: () => onCreateReport(site.id),
                          }
                        : undefined
                  }
                  secondaryAction={
                    onPlanSite
                      ? {
                          label: 'Data sopralluogo',
                          icon: 'event',
                          onClick: () => onPlanSite(site),
                        }
                      : undefined
                  }
                  size="large"
                />
              );
            })}
          </div>
        )
      ) : (
        <Panel>
          <EmptyState
            icon="factory"
            title="Nessuno stabilimento"
            description="Aggiungi il primo stabilimento per questa azienda."
            variant="inline"
          />
        </Panel>
      )}
    </Section>
  );
}
